<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { dataManager, type QuestionnaireUserData } from '@/dataManager'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors } from 'chart.js'
import { CircleProgressBar } from 'circle-progress.vue'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors)

const emit = defineEmits<{
  (e: 'backButton', backUrl: string|null): void
}>()

const props = defineProps<{
  userData: QuestionnaireUserData
}>()

const router = useRouter()

const dm = dataManager.getInstance()

const numDone = computed(() => {
  return calcNumDone(props.userData)
})

const numPending = computed(() => {
  return calcNumPending(props.userData)
})

const allPending = computed(() => {
  return numPending.value == dm.getMainQuestionnaireList().length
})

const percentDone = computed(() => {
  return numDone.value * 100 / dm.getMainQuestionnaireList().length
})

const chartData = computed(() => {
  return calcChartData(props.userData)
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      grid: {
        drawOnChartArea: false
      }
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 45
      },
      grid: {
        drawOnChartArea: false
      }
    }
  },
}

onMounted(() => {
  emit('backButton', null)
  if (!dm.welcomeMessageAlreadyShown) {
    setTimeout(function() {
      let dialog = document.querySelector('#welcome-message') as HTMLDialogElement
      if (dialog) {
        dialog.showModal()
        dm.welcomeMessageAlreadyShown = true;
      }
    }, 1000)
  }
})

function calcNumDone(userData: QuestionnaireUserData): number {
  return dm.getDoneQuestionnaires().length
}

function calcNumPending(userData: QuestionnaireUserData): number {
  return dm.getPendingQuestionnaires().length
}

function calcChartData(userData: QuestionnaireUserData) {
  let labels: string[] = []
  let data: number[] = []
  dm.getMainQuestionnaireList().forEach((questionnaire) => {
    let lastDone = dm.getLastQuestionnaireRun(questionnaire.id)
    if (lastDone != null) {
      let [mainTitle, subQuestionnaireTitle] = dm.extractTitles(questionnaire.title)
      labels.push(mainTitle)
      data.push(dm.normalizeToPercentage(lastDone))
    }
  })
  return {
    labels: labels,
    datasets: [{
      data: data,
      borderRadius: 6,
      backgroundColor: '#10acc5'
    }]
  }
}

function randomChartData() {
  let labels: string[] = []
  let data: number[] = []
  dm.getQuestionnaireList().forEach((questionnaire) => {
    labels.push(questionnaire.title)
    data.push(getRandomInt(0, 100))
  })
  return {
    labels: labels,
    datasets: [{
      data: data,
      borderRadius: 6,
      backgroundColor: '#10acc5'
    }]
  }
}

function getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

function closeWelcomeDialog(): void {
  (document.querySelector('#welcome-message') as HTMLDialogElement).close()
}

function goToNewQuestionnaire() {
  router.push('/questionnaires')
}
</script>

<template>
<div class="wrapper-center">
<div class="wrapper">
  <h2>¡Bienvenido!</h2>

  <CircleProgressBar :value="percentDone" :max="100"
    :rounded="true"
    color-unfilled="#ff6800"
    :percentage="true"
    stroke-width="14"
    title="Tu progreso"
    />

<!--
  <p v-if="allPending">¡Empieza con tu primer cuestionario!</p>
  <p v-else-if="numPending >= 1">Vas muy bien, pero aún tienes cuestionarios pendientes de realizar</p>
  <p v-else>¡Llevas todo al día! Puedes repetir algún cuestionario si quieres.</p>
-->

  <RouterLink to="/questionnaires" class="button">Cubrir un cuestionario</RouterLink>

  <div v-if="userData.questionnaireRuns.length > 0">
    <h3>Tus últimos resultados</h3>
    <Bar
      class="summary-chart"
      :options="chartOptions"
      :data="chartData"
    />
  </div>

  <div v-else class="random-placeholder">
    <Bar
      class="summary-chart"
      :options="chartOptions"
      :data="randomChartData()"
    />
    <div class="message">
      <p><b>Comienza a realizar cuestionarios para ver tu progreso.</b></p>
    </div>
  </div>

  <RouterLink to="/stats" class="button secondary">Estadísticas detalladas</RouterLink>
  <RouterLink to="/resources/recommended" class="button secondary">Revisa tus recursos recomendados</RouterLink>
  <RouterLink to="/resources/all" class="button secondary">Consulta nuestro catálogo de recursos</RouterLink>

  <dialog id="welcome-message" closedby="any">
    <div class="container">
      <h2>¡Bienvenido!</h2>
      <p v-if="allPending">¡Empieza con tu primer cuestionario!</p>
      <p v-else-if="numPending > 1">Vas muy bien, pero tienes cuestionarios pendientes de realizar</p>
      <p v-else>¡Llevas todo al día! Puedes repetir algún cuestionario si quieres.</p>

      <p v-if="dm.getCurrentUserData().importantDates.length == 0">
        Recuerda que en el apartado <b>fechas señaladas</b>
        puedes anotar el inicio o los cambios en tus tratamientos para valorar tu
        evolución.
      </p>

      <RouterLink to="/questionnaires" class="button">Cubrir un cuestionario</RouterLink>
      <button @click="closeWelcomeDialog()" class="secondary">Más tarde</button>
    </div>
  </dialog>
</div>
</div>
</template>

<style scoped>

.summary-chart {
  max-height: 250px;
  min-height: 250px;
}

.random-placeholder {
  position: relative;
  .message {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fffb;

    padding: 20px;

    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.wrapper-center {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
  align-items: center;
}

.wrapper {
  width: 100%;
  max-width: var(--content-max-width);
}

.wrapper > * {
  flex-shrink: 0;
}

h2, p {
  text-align: center;
}

button, a.button {
  width: 100%;
}

a {
  text-align: center;
  padding: 10px;
  margin: 5px 0;
}

.circle-progress {
  font-family: var(--title-font);
  color: var(--secondary-color);
  font-size: 16pt;
}
</style>
